import { isPlatformServer, isPlatformBrowser } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, PLATFORM_ID, Injectable, Inject, NgModule } from '@angular/core';
import '@angular/localize/init';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import * as i1 from '@fleetmatics/ui.utilities';
import * as i2 from '@angular/material/legacy-snack-bar';

/*
 * Copyright © 2020-2023. Verizon Connect Ireland Limited. All rights reserved.
 */
const COOKIE_BANNER_WRAPPER = new InjectionToken('cookieBannerWrapperLoader');
const LOCAL_STORAGE = new InjectionToken('localStorage');
const DISABLE_BANNER = new InjectionToken('disableBanner');
const COOKIES_ACCEPTED = 'CookiesAccepted';
class CookieBannerConnectorService {
    constructor(cookie, snackBar, platformId, cookieBannerWrapper, localStorage, disableBanner) {
        this.cookie = cookie;
        this.snackBar = snackBar;
        this.platformId = platformId;
        this.cookieBannerWrapper = cookieBannerWrapper;
        this.localStorage = localStorage;
        this.disableBanner = disableBanner;
        this.isAcceptedSubject$ = new BehaviorSubject(!this.shouldShowBanner);
        this.isAccepted$ = this.isAcceptedSubject$.asObservable();
    }
    start() {
        if (this.disableBanner || isPlatformServer(this.platformId)) {
            return;
        }
        if (this.shouldShowBanner) {
            this.openBanner();
        }
    }
    get legacyValue() {
        return this.cookie.getCookie(COOKIES_ACCEPTED) === 'True';
    }
    get value() {
        try {
            return JSON.parse(this.localStorage.getItem(COOKIES_ACCEPTED));
        }
        catch (e) {
            return false;
        }
    }
    set value(value) {
        this.localStorage.setItem(COOKIES_ACCEPTED, value.toString());
        this.isAcceptedSubject$.next(value);
    }
    get shouldShowBanner() {
        return !this.legacyValue && !this.value;
    }
    async openBanner() {
        const componentEsm = await this.cookieBannerWrapper();
        const componentKey = Object.keys(componentEsm)[0];
        const component = componentEsm[componentKey];
        const banner = this.snackBar.openFromComponent(component, { duration: 0, panelClass: 'vzc-snack-bar-banner' });
        banner
            .onAction()
            .pipe(take(1)) // take(1) will complete the subscription after it emits
            .subscribe(_ => (this.value = true)); // so no need to unsubscribe
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: CookieBannerConnectorService, deps: [{ token: i1.FmCookieService }, { token: i2.MatLegacySnackBar }, { token: PLATFORM_ID }, { token: COOKIE_BANNER_WRAPPER }, { token: LOCAL_STORAGE }, { token: DISABLE_BANNER }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: CookieBannerConnectorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: CookieBannerConnectorService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.FmCookieService }, { type: i2.MatLegacySnackBar }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [COOKIE_BANNER_WRAPPER]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCAL_STORAGE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DISABLE_BANNER]
                }] }]; } });

/*
 * Copyright © 2020-2023. Verizon Connect Ireland Limited. All rights reserved.
 */
function localStorageFactory(platformId) {
    if (isPlatformBrowser(platformId)) {
        return window?.localStorage;
    }
    return;
}
class CookieBannerConnectorModule {
    static forRoot(cookieBannerWrapper, disableBanner = false) {
        return {
            ngModule: CookieBannerConnectorModule,
            providers: [
                { provide: COOKIE_BANNER_WRAPPER, useValue: cookieBannerWrapper },
                {
                    provide: LOCAL_STORAGE,
                    useFactory: localStorageFactory,
                    deps: [PLATFORM_ID],
                },
                {
                    provide: DISABLE_BANNER,
                    useValue: disableBanner,
                },
            ],
        };
    }
    constructor(service) {
        service.start();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: CookieBannerConnectorModule, deps: [{ token: CookieBannerConnectorService }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.8", ngImport: i0, type: CookieBannerConnectorModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: CookieBannerConnectorModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: CookieBannerConnectorModule, decorators: [{
            type: NgModule
        }], ctorParameters: function () { return [{ type: CookieBannerConnectorService }]; } });

/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */

/*
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { COOKIE_BANNER_WRAPPER, CookieBannerConnectorModule, CookieBannerConnectorService, DISABLE_BANNER, LOCAL_STORAGE, localStorageFactory };
